import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, Smartphone } from 'lucide-react';
import { OTP_FAILURES, otpChannels, sendOtp, verifyOtp, type IssuedOtp, type OtpChannel } from '@/api/otp';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/lib/toast';

const NAME = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' } as const;
const ICON = { whatsapp: MessageCircle, sms: Smartphone, email: Mail } as const;

/**
 * Check that a code reaches you on each channel (owner, 2026-09-21: "let's test all 3"). Sends a
 * verification code - purpose step_up, to YOUR OWN recorded contact, one channel at a time - and
 * checks it when you type it back. Sign-in cannot do this: its phone hook always tries WhatsApp
 * first and falls back to SMS, so only here is SMS tested on its own.
 *
 * THE APP OWNER ONLY (owner, 2026-09-26: "limit test only to super admin guard and screen"). It is
 * a diagnostic for whoever configures delivery, and it sat on the dashboard where every field
 * officer and viewer met it first thing. Each press also spends a real message against a real
 * provider, so it is not a button to leave where somebody will press it to see what it does.
 *
 * The guard is HERE rather than at the call site, so the panel cannot be rendered unguarded by
 * whoever places it on a screen next.
 */
export function ContactChannelTest() {
  const { profile } = useAuth();
  // The guard returns before the panel's hooks run, so they live in a component of their own: a
  // profile that arrives after the first render would otherwise change the hook count and throw.
  if ((profile?.tier ?? 99) > 0) return null;
  return <ContactChannelPanel />;
}

function ContactChannelPanel() {
  const channels = useQuery({ queryKey: ['otp-channels', 'me'], queryFn: () => otpChannels(), enabled: isSupabaseConfigured });
  const [issued, setIssued] = useState<(IssuedOtp & { via: OtpChannel['channel'] }) | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Partial<Record<OtpChannel['channel'], 'sent' | 'verified' | 'failed'>>>({});

  async function send(channel: OtpChannel['channel']) {
    setBusy(true);
    try {
      const result = await sendOtp({ purpose: 'step_up', channel });
      setIssued({ ...result, via: channel });
      setCode('');
      setResults((now) => ({ ...now, [channel]: 'sent' }));
      toast.success(`${NAME[channel]} code sent to ${result.destination_masked}.`);
    } catch (error) {
      setResults((now) => ({ ...now, [channel]: 'failed' }));
      toast.error(`${NAME[channel]}: ${error instanceof Error ? error.message : 'not sent'}`);
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    if (!issued) return;
    setBusy(true);
    try {
      const result = await verifyOtp(issued.otp_event_id, code);
      if (result.verified) {
        setResults((now) => ({ ...now, [issued.via]: 'verified' }));
        toast.success(`${NAME[issued.via]} works: the code arrived and matched.`);
        setIssued(null);
      } else {
        toast.error(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code did not match.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card channel-test">
      <div className="section-heading"><h3>Test my contact channels</h3></div>
      <p className="muted" style={{ margin: 0, fontSize: '12.5px' }}>
        Sends a code to your own recorded contact on one channel. Type it back to confirm it arrived.
      </p>
      <ul className="dash-list">
        {(channels.data ?? []).map((entry) => {
          const Icon = ICON[entry.channel];
          const state = results[entry.channel];
          return (
            <li key={entry.channel}>
              <span><Icon size={14} /> {NAME[entry.channel]} <small className="muted">{entry.destination_masked ?? 'not recorded'}</small></span>
              <span className="channel-test-actions">
                {state && <small className={`channel-state ${state}`}>{state === 'verified' ? 'Works' : state === 'sent' ? 'Sent' : 'Failed'}</small>}
                <button disabled={busy || !entry.available} onClick={() => void send(entry.channel)} type="button">Send test code</button>
              </span>
            </li>
          );
        })}
      </ul>
      {issued && (
        <div className="pin-row">
          <input autoFocus className="login-code" inputMode="numeric" maxLength={6}
                 onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                 placeholder={`Code from ${NAME[issued.via]}`} value={code} />
          <button className="primary" disabled={busy || code.length !== 6} onClick={() => void check()} type="button">Check code</button>
        </div>
      )}
    </section>
  );
}
